import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { Icon } from "../../components/Component";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import { expensesApis } from "../../api/Expenses";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import {
  Table,
  Card,
  Empty,
  Tag,
} from "antd";
import { ColumnType } from "antd/es/table";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../utils/skeleton-templates/table";

interface PendingAdvance {
  id: number;
  employeeName: string;
  amount: number;
  purpose: string;
  requestDate: string;
  project: string;
  description: string;
  status: string;
  paymentType: string;
  trip: string;
  reference: string;
  notes: string;
  paidThrough: string;
  recordedBy: string;
  comments: Array<{
    id: number;
    author: string;
    text: string;
    date: string;
  }>;
  history: Array<{
    id: number;
    author: string;
    action: string;
    date: string;
  }>;
  projectId?: number;
  expenseReportId?: number;
  expenseId?: number;
}

// Helper function to transform API data to component format
const transformApiDataToAdvance = (apiData: any): PendingAdvance => {
  return {
    id: apiData.id || apiData.expenseId || Math.random(),
    employeeName: String(apiData.paymentFromName || apiData.employeeName || apiData.userName || apiData.createdBy || "Unknown User"),
    amount: parseFloat(apiData.amount || 0),
    purpose: String(apiData.purpose || apiData.description || "Payment Advance"),
    requestDate: String(apiData.requestDate || apiData.actionDate || apiData.createdAt || dayjs().format('DD/MM/YYYY')),
    project: String(apiData.projectName || apiData.project || "Default Project"),
    description: String(apiData.description || apiData.purpose || "Payment Advance"),
    status: String(apiData.paymentStatus || apiData.status || "Pending"),
    paymentType: String(apiData.paymentType || apiData.category || "Advance"),
    trip: String(apiData.trip || ""),
    reference: String(apiData.referenceId || apiData.reference || ""),
    notes: String(apiData.notes || ""),
    paidThrough: String(apiData.paymentMode || apiData.paidThrough || "Petty Cash"),
    recordedBy: String(apiData.recordedBy || apiData.createdBy || "System"),
    projectId: apiData.projectId,
    expenseReportId: apiData.expenseReportId,
    expenseId: apiData.expenseId || apiData.id,
    comments: apiData.comments || [
      {
        id: 1,
        author: "System",
        text: "Payment advance created",
        date: dayjs().format('DD/MM/YYYY')
      }
    ],
    history: apiData.history || [
      {
        id: 1,
        author: String(apiData.recordedBy || apiData.createdBy || "System"),
        action: `An Advance of ₹${parseFloat(apiData.amount || 0).toLocaleString()}.00 was recorded`,
        date: dayjs().format('DD/MM/YYYY HH:mm:ss')
      }
    ]
  };
};


const PaymentAdvances: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ projectId: string }>();
  const projectIdFromUrl = params.projectId;

  // Check if user came from ReportBreakDown
  const fromReportBreakDown = location.state?.fromReportBreakDown || false;

  const [pendingAdvancesList, setPendingAdvancesList] = useState<PendingAdvance[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [projectId, setProjectId] = useState<string | number | null>(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["10", "15", "20", "30", "50"],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [hasProjectId, setHasProjectId] = useState(false);

  // Removed modal state management - now using full-screen navigation

  useEffect(() => {
    // Initialize menu data for expenses pages
  }, [location]);

  // Centralized function to fetch advances data
  const fetchAdvancesData = async (projectId: string | number | null | undefined, pageSize: number = pagination.pageSize) => {
    // Validate projectId - must be a valid number
    const numericProjectId = projectId ? Number(projectId) : null;
    if (!numericProjectId || isNaN(numericProjectId)) {
      setHasProjectId(false);
      setPendingAdvancesList([]);
      setPagination(prev => ({ ...prev, total: 0 }));
      return;
    }

    setProjectId(numericProjectId);
    setHasProjectId(true);
    setLoading(true);

    try {
      // Fetch only advance payments using paymentType parameter with pagination
      const params = {
        projectId: numericProjectId,
        pageSize: pageSize,
        pageDiff: 0,
        sortOrder: 'DESC',
        direction: 'P',
        paymentType: 'Advance'
      };

      const response = await expensesApis.fetchExpenses(numericProjectId, params);
      if (response && response.status && response.data) {
        // Transform the API response to component format
        const apiAdvances = response.data.list || response.data || [];
        const totalCount = response.data?.count || 0;
        const transformedAdvances = apiAdvances.map((advance: any) => {
          return transformApiDataToAdvance(advance);
        });

        setPendingAdvancesList(transformedAdvances);

        // Update pagination total
        setPagination(prev => ({
          ...prev,
          total: totalCount
        }));
      } else {
        // Handle empty response
        setPendingAdvancesList([]);
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
      }
    } catch (error) {
      // Set empty data on error
      setPendingAdvancesList([]);
      setPagination(prev => ({
        ...prev,
        total: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  // Consolidated data fetching effect - prevents multiple API calls
  useEffect(() => {
    const fetchAdvances = async () => {
      // Extract projectId from URL params first, then location state
      const { id: projectIdFromState, projectId: projectIdAlt } = location.state || {};
      const resolvedProjectId = projectIdFromUrl || projectIdFromState || projectIdAlt;

      await fetchAdvancesData(resolvedProjectId);
    };

    fetchAdvances();
  }, [location.state, pagination.pageSize, projectIdFromUrl]);

  useEffect(() => {
    const checkMobileView = () => {
      setMobileView(window.innerWidth < 768);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);

    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  useEffect(() => {
    sm
      ? document.body.classList.add("toggle-shown")
      : document.body.classList.remove("toggle-shown");
  }, [sm]);

  // Resolve current projectId for navigation (never use null in URL)
  const navProjectId = projectIdFromUrl || (location.state as any)?.id || (location.state as any)?.projectId || projectId;
  const withProjectIdPath = (basePath: string) =>
    navProjectId != null && !isNaN(Number(navProjectId)) ? `${basePath}/${navProjectId}` : basePath;
  const paymentAdvancesBackUrl = withProjectIdPath('/pms/expenses/payment-advances');

  const handleAddNew = () => {
    // Navigate to NewAdvance page with full-screen layout (path always with projectId when valid)
    const path = withProjectIdPath('/pms/expenses/new-advance');
    navigate(path, {
      state: {
        ...location.state,
        fromPaymentAdvances: true,
        backUrl: paymentAdvancesBackUrl
      }
    });
  };

  const handleEdit = async (advance: PendingAdvance) => {
    try {
      setLoading(true);

      // Fetch full advance data using the API
      if (advance.expenseId) {
        const response = await expensesApis.fetchPayment(advance.expenseId);

        if (response && response.status && response.data) {
          const fullAdvanceData = response.data;

          // Navigate to NewAdvance page in edit mode with full-screen layout
          const path = withProjectIdPath('/pms/expenses/new-advance');
          navigate(path, {
            state: {
              ...location.state,
              advanceData: fullAdvanceData,
              isEditMode: true,
              fromPaymentAdvances: true,
              backUrl: paymentAdvancesBackUrl
            }
          });
        } else {
          Swal.fire({
            title: "Error!",
            text: "Failed to fetch advance data for editing",
            icon: "error",
            confirmButtonText: "OK"
          });
        }
      } else {
        // If no expenseId, navigate with the existing advance data
        const path = withProjectIdPath('/pms/expenses/new-advance');
        navigate(path, {
          state: {
            ...location.state,
            advanceData: advance,
            isEditMode: true,
            fromPaymentAdvances: true,
            backUrl: paymentAdvancesBackUrl
          }
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "An error occurred while fetching advance data",
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLinkToReport = async (advance: PendingAdvance) => {
    try {
      setLoading(true);

      // Fetch full advance data using the API
      if (advance.expenseId) {
        const response = await expensesApis.fetchPayment(advance.expenseId);

        if (response && response.status && response.data) {
          const fullAdvanceData = response.data;

          // Navigate to NewAdvance page in link mode with full-screen layout
          const path = withProjectIdPath('/pms/expenses/new-advance');
          navigate(path, {
            state: {
              ...location.state,
              advanceData: fullAdvanceData,
              linkToReportMode: true,
              fromPaymentAdvances: true,
              backUrl: paymentAdvancesBackUrl
            }
          });
        } else {
          Swal.fire({
            title: "Error!",
            text: "Failed to fetch advance data for linking",
            icon: "error",
            confirmButtonText: "OK"
          });
        }
      } else {
        // If no expenseId, navigate with the existing advance data
        const path = withProjectIdPath('/pms/expenses/new-advance');
        navigate(path, {
          state: {
            ...location.state,
            advanceData: advance,
            linkToReportMode: true,
            fromPaymentAdvances: true,
            backUrl: paymentAdvancesBackUrl
          }
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "An error occurred while fetching advance data",
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setLoading(false);
    }
  };

  // Removed modal-related functions - now using full-screen navigation


  const handleBack = () => {
    // Check if we came from ReportBreakDown and have a backUrl
    if (location.state?.fromReportBreakDown && location.state?.backUrl) {
      navigate(location.state.backUrl, {
        state: {
          projectId: location.state.projectId,
          reportId: location.state.reportId,
          reportName: location.state.reportName
        }
      });
    } else {
      navigate(-1);
    }
  };

  const handleDeleteAdvance = async (advance: PendingAdvance) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        // Call the delete API if the advance has an expenseId
        if (advance.expenseId) {
          const response = await expensesApis.deletePaymentIn(advance.expenseId);

          if (response && response.status) {
            // Refresh data from API
            await refreshData();

            Swal.fire("Deleted!", "Payment advance has been deleted.", "success");
          } else {
            throw new Error(response?.message || "Failed to delete payment advance");
          }
        } else {
          // For local-only advances (no API call needed)
          // Refresh data from API
          await refreshData();

          Swal.fire("Deleted!", "Payment advance has been deleted.", "success");
        }
      } catch (error) {
        Swal.fire({
          title: "Error!",
          text: error instanceof Error ? error.message : "Failed to delete payment advance",
          icon: "error",
          confirmButtonText: "OK"
        });
      } finally {
        setIsLoading(false);
      }
    }
  };


  // Refresh function to reload data
  const refreshData = async () => {
    const { id: projectIdFromState, projectId: projectIdAlt } = location.state || {};
    const currentProjectId = projectIdFromState || projectIdAlt;
    if (!currentProjectId) return;

    await fetchAdvancesData(currentProjectId);
  };

  // Table change handler (similar to Reports.tsx)
  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const { id: projectIdFromState, projectId: projectIdAlt } = location.state || {};
    const currentProjectId = projectIdFromState || projectIdAlt;
    if (!currentProjectId) return;

    // Update pagination state
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });

    // Fetch data with new pagination parameters
    await fetchAdvancesData(currentProjectId, newPagination.pageSize);
  };



  // Column definitions for the advances table (similar to Reports.tsx)
  const advanceColumns: ColumnType<PendingAdvance>[] = [
    {
      title: "Amount From",
      dataIndex: "employeeName",
      key: "employeeName",
      width: 150,
      render: (text: string) => (
        <div style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif' }}>
          {text}
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "requestDate",
      key: "requestDate",
      width: 120,
      render: (text: string) => (
        <div style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif' }}>
          {text}
        </div>
      ),
    },
    {
      title: "Purpose",
      dataIndex: "purpose",
      key: "purpose",
      width: 200,
      render: (text: string) => (
        <div style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif' }}>
          {text}
        </div>
      ),
    },
    {
      title: "Link Status",
      dataIndex: "expenseReportId",
      key: "linkStatus",
      width: 100,
      render: (expenseReportId: number | string | undefined) => {
        const isLinked = expenseReportId && expenseReportId !== null && expenseReportId !== undefined;
        return (
          <div style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif' }}>
            {isLinked ? 'Linked' : 'Unlinked'}
          </div>
        );
      },
    },
    {
      title: "Payment Type",
      dataIndex: "paymentType",
      key: "paymentType",
      width: 120,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (amount: number) => (
        <div style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif', fontWeight: 'bold' }}>
          ₹{amount.toLocaleString()}.00
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <Tag
          color={
            status.toLowerCase().includes("draft") ? "geekblue" :
              status.toLowerCase().includes("pending") ? "gold" :
                status.toLowerCase().includes("approved") ? "green" :
                  status.toLowerCase().includes("rejected") ? "red" : "default"
          }
          style={{ fontSize: 14, padding: '2px 12px' }}
        >
          {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_: any, record: PendingAdvance) => {
        const isLinked = record.expenseReportId && record.expenseReportId !== null && record.expenseReportId !== undefined;

        return (
          <UncontrolledDropdown>
            <DropdownToggle tag="a" className="dropdown-toggle btn btn-icon btn-trigger" onClick={(e) => e.stopPropagation()}>
              <Icon name="more-h" />
            </DropdownToggle>
            <DropdownMenu>
              {!isLinked && (
                <DropdownItem onClick={(e) => {
                  e.stopPropagation();
                  handleLinkToReport(record);
                }}>
                  <Icon name="link" className="me-2" size="sm" />
                  Link to Report
                </DropdownItem>
              )}
              <DropdownItem onClick={(e) => {
                e.stopPropagation();
                handleEdit(record);
              }}>
                <Icon name="edit" className="me-2" size="sm" />
                Edit
              </DropdownItem>
              <DropdownItem className="text-danger" onClick={(e) => {
                e.stopPropagation();
                handleDeleteAdvance(record);
              }}>
                <Icon name="trash" className="me-2" size="sm" />
                Delete
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
    },
  ];

  return (
    <>
      <Head title="Payment Advances" />

      <Content>
        <Card
          className="card-bordered"
          bodyStyle={{ padding: "0" }}
        >
          <div className="card-inner card-inner-lg" style={{ minHeight: "87vh" }}>

              <div
                className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
                style={{ minHeight: "40px", border: "0px", marginBottom: "20px" }}
              >
                <h5 style={{ marginBottom: "0px", marginTop: "0px" }}>Payment Advances</h5>
                <div className="toggle-wrap nk-block-tools-toggle">
                  <ul className="nk-ibx-head-tools g-1 m-0 p-0 d-flex align-items-center">
                    <li className="pt-0 pb-0">
                      <Button
                        className="toggle"
                        color="primary"
                        size="sm"
                        onClick={handleAddNew}
                        disabled={isLoading}
                        style={{ borderRadius: "8px", height: "32px", fontSize: '14px', padding: '4px 12px', fontWeight: "500", fontFamily: 'Inter, Roboto, sans-serif' }}
                      >
                        <Icon name="plus" />
                        <span>New Payment In</span>
                      </Button>
                    </li>
                    <li>
                      <UncontrolledDropdown>
                        <DropdownToggle
                          tag="a"
                          className="dropdown-toggle btn btn-icon btn-trigger"
                          onClick={refreshData}
                        >
                          <Icon name="redo"></Icon>
                        </DropdownToggle>
                      </UncontrolledDropdown>
                    </li>
                    {fromReportBreakDown && (
                      <li className="pt-0 pb-0">
                        <Button
                          className="toggle"
                          color="primary"
                          size="sm"
                          onClick={handleBack}
                        >
                          <Icon name="arrow-left" />
                          <span>Back</span>
                        </Button>
                      </li>
                    )}
                  </ul>
                </div>
              </div>

              {/* Show message when no projectId is available */}
              {!hasProjectId && (
                <div className="alert alert-warning" style={{ margin: '20px', borderRadius: '8px' }}>
                  <div className="d-flex align-items-center">
                    <Icon name="alert-triangle" className="me-2" style={{ fontSize: '18px', color: '#f59e0b' }} />
                    <div>
                      <strong>No Project Selected</strong>
                      <p className="mb-0" style={{ fontSize: '14px', marginTop: '4px' }}>
                        Please select a project from the main dashboard to view payment advances.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {hasProjectId && (
                <div className="card-inner card-inner-lg p-0" style={{ marginTop: "20px" }}>
                  <div className="content clearfix">
                    <div
                      className="table-responsive"
                      style={{
                        width: '100%',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        maxWidth: '100%',
                        // border: '1px solid #e5e5e5',
                        borderRadius: '4px'
                      }}
                    >
                      <SkeletonTable
                        loading={loading}
                        columns={advanceColumns as SkeletonTableColumnsType[]}
                      >
                        <Table
                          rowKey="id"
                          columns={advanceColumns}
                          dataSource={pendingAdvancesList}
                          pagination={pagination}
                          onChange={tableOnChange}
                          bordered
                          size="middle"
                          scroll={{
                            x: 880,
                          }}
                          style={{
                            minWidth: '880px',
                            width: '100%'
                          }}
                          locale={{
                            emptyText: (
                              <div style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "200px",
                                backgroundColor: "white",
                              }}>
                                <Empty description="No Data Found " />
                              </div>
                            ),
                          }}
                        />
                      </SkeletonTable>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </Card>
      </Content>
    </>
  );
};

export default PaymentAdvances;